import { usePagination, DOTS } from "../../../hooks/usePagination";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

type Props = {
  onPageChange: (arg: number | string) => void;
  totalCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize: number;
};

const cell =
  "mx-1 my-auto box-border flex h-[38px] min-w-[38px] items-center justify-center rounded-[10px] px-3 text-[14px] font-semibold transition-colors";

const Pagination = ({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
}: Props) => {
  const paginationRange = usePagination(
    totalCount,
    pageSize,
    siblingCount,
    currentPage
  );

  // If there are not multiple pages to display we will not render this component
  if (currentPage === 0 || !paginationRange || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => onPageChange(currentPage + 1);
  const onPrevious = () => onPageChange(currentPage - 1);
  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <ul className="mb-14 mt-10 flex w-full list-none justify-center">
      {/* Left navigation arrow */}
      <li
        className={`${cell} border border-line bg-white text-ink hover:cursor-pointer hover:border-secondary-400 ${
          currentPage === 1
            ? "pointer-events-none border-line text-muted-300 hover:cursor-default"
            : ""
        }`}
        onClick={onPrevious}
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </li>
      {paginationRange.map((pageNumber, index) => {
        if (pageNumber === DOTS) {
          return (
            <li key={index} className={`${cell} text-muted-300`}>
              &#8230;
            </li>
          );
        }
        const active = pageNumber === currentPage;
        return (
          <li
            key={index}
            className={`${cell} border hover:cursor-pointer ${
              active
                ? "border-secondary-400 bg-secondary-400 text-white"
                : "border-line bg-white text-ink hover:border-secondary-400"
            }`}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </li>
        );
      })}
      {/* Right navigation arrow */}
      <li
        className={`${cell} border border-line bg-white text-ink hover:cursor-pointer hover:border-secondary-400 ${
          currentPage === lastPage
            ? "pointer-events-none border-line text-muted-300 hover:cursor-default"
            : ""
        }`}
        onClick={onNext}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </li>
    </ul>
  );
};

export default Pagination;
